import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import ReactDOMServer from 'react-dom/server';
import {
  AppDatePicker,
  AppInput,
  AppModal,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import Translator from '../../../../../../components/helpers/Translator';
import * as currentPageAction from '../../../../../../../store/actions/currentPageAction';
import { axiosPath } from '../../../../../../../routing/helpers/router';
import errorHandler from '@/layout/components/error/ErrorHandler';
import ReviewFields from './ReviewFields';
import useIsMounted from '../../../../../../components/hooks/useIsMounted';
import AppSpinner from '../../../../../../../layout/components/AppSpinner';
import minions from '../../../../../../../minions/minions';
import _ from 'lodash';

export default function ReviewEdit({ elementID, review, onSave }) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const locale = useSelector((state) => state.global?.urlData?.lang);
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge
  );
  const level1 = useSelector(
    (state) => state.currentPage?.elementLevels?.level1
  );
  const controlLevel1 = useSelector(
    (state) => state.currentPage?.elementLevels?.level1?.[elementID]
  );
  const [submitType, setSubmitType] = useState();
  const [addedReview, setAddedReview] = useState();
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [noYesOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-legalCompliance" name="no" />
      ),
      value: false,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-legalCompliance" name="yes" />
      ),
      value: true,
    },
  ]);

  // > SAVE
  const handleSave = useCallback(
    async (values) => {
      if (submitType === 'add-review') {
        return axios
          .post(
            axiosPath(
              "api_r_user_module_totalEnergiesRisk_control_review_validate"
            ),
            minions.forms.toFormData({
              moduleBridgeHex,
              reviewHex: review?.hex,
              ...values,
            }),
            { headers: { "Content-Type": "multipart/form-data" } }
          )
          .then(({ data }) => {
            if (isMounted.current) {
              setAddedReview(data);
              setShowValidateModal(true);
              setSubmitType(undefined);

              controlLevel1.scheduledReviewDate = data?.scheduledReviewDate;

              dispatch(
                currentPageAction.change("elementLevels", {
                  level1: { ...level1, [elementID]: controlLevel1 },
                })
              );
            }
          })
          .catch((e) => dispatch(errorHandler(e)));
      }
      if (submitType === 'validate') {
        return axios
          .post(
            axiosPath(
              "api_r_user_module_totalEnergiesRisk_control_review_validate"
            ),
            minions.forms.toFormData({
              moduleBridgeHex,
              reviewHex: review?.hex,
              ...values,
            }),
            { headers: { "Content-Type": "multipart/form-data" } }
          )
          .then(() => {
            setSubmitType(undefined);
            onSave();
          })
          .catch((e) => dispatch(errorHandler(e)));
      }

      if (submitType === 'draft') {
        return axios
          .post(
            axiosPath(
              "api_r_user_module_totalEnergiesRisk_control_review_edit"
            ),
            minions.forms.toFormData({
              moduleBridgeHex,
              reviewHex: review?.hex,
              ...values,
            }),
            { headers: { "Content-Type": "multipart/form-data" } }
          )
          .then(() => {
            setSubmitType(undefined);
            onSave();
          })
          .catch((e) => dispatch(errorHandler(e)));
      }

      return axios
        .post(
          axiosPath("api_r_user_module_totalEnergiesRisk_control_review_edit"),
          minions.forms.toFormData({
            moduleBridgeHex,
            reviewHex: addedReview?.hex,
            ...values,
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then(() => {
          setSubmitType(undefined);
          onSave();
        })
        .catch((e) => dispatch(errorHandler(e)));
    },
    [moduleBridgeHex, review, submitType]
  );
  // < SAVE

  // > EDIT ADDED REVIEW
  const handleEditAddedReview = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath("api_r_user_module_totalEnergiesRisk_control_review_edit"),
          minions.forms.toFormData({
            moduleBridgeHex,
            reviewHex: addedReview?.hex,
            ...values,
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setAddedReview(undefined);
            setShowValidateModal(false);

            controlLevel1.scheduledReviewDate = data?.scheduledReviewDate;

            dispatch(
              currentPageAction.change(
                "elementLevels",
                {
                  level1: { ...level1, [elementID]: controlLevel1 },
                },
                true
              )
            );

            onSave();
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, addedReview]
  );
  // < EDIT ADDED REVIEW

  const showValidateBtn = useCallback(
    () =>
      (review?.state === 1 && review?.responsibleID === userBridge?.id) ||
      (review?.state === 2 && userBridge?.permissions?.level === 0),
    [review, userBridge]
  );

  const showSaveDraftBtn = useCallback(
    () =>
      (userBridge?.permissions?.level === 0 &&
        (review?.state === 0 || review?.state === 2)) ||
      (review?.responsibleID === userBridge?.id && review?.state === 1),
    [review, userBridge]
  );

  return (
    <>
      <Formik
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={() =>
          Yup.lazy(() => {
            if (review.state === 1) {
              return Yup.object().shape({
                responsibleAnswer: Yup.number()
                  .nullable()
                  .required(
                    ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="required"
                      />
                    )
                  ),
                files: Yup.array().test(
                  'files',
                  'Se debe adjuntar al menos un documento',
                  function(v) {
                    return (
                      !this.parent.requireDocumentation ||
                      (this.parent.requireDocumentation &&
                        (this.parent?.files?.length > 0 ||
                          (!_.isEmpty(review?.files) &&
                            !_.isEqual(
                              _.sortBy(Object.keys(review.files)),
                              _.sortBy(this.parent.deletedFiles)
                            ))))
                    );
                  }
                ),
              });
            }
            if (review.state === 2) {
              return Yup.object().shape({
                controlReviewValidationResult: Yup.number()
                  .nullable()
                  .required(
                    ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="required"
                      />
                    )
                  ),
              });
            }
            return Yup.object();
          })
        }
        initialValues={{
          hex: review?.hex,
          scheduledReviewDate: review?.scheduledReviewDate ?? '',
          description: review?.description ?? '',
          announced: review?.announced,
          announcementDate: review?.announcementDate,
          state: review?.state,
          responsibleID: review?.responsibleID,
          responsibleAnswer: review?.responsibleAnswer,
          responsibleObservations: review?.responsibleObservations,
          files: [],
          deletedFiles: [],
          requireDocumentation:
            review?.requireDocumentation !== null
              ? review?.requireDocumentation
              : review?.controlDocumentation === 2,
          controlState: review?.controlState,
          responsibleAnswerDate: review?.responsibleAnswerDate,
          complianceAreaCommunicationDate:
            review?.complianceAreaCommunicationDate,
          controlReviewValidationResult: review?.controlReviewValidationResult,
          complianceAreaObservations: review?.complianceAreaObservations,
          complianceAreaValidationDate: review?.complianceAreaValidationDate,
        }}
        onSubmit={handleSave}
      >
        {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
          <>
            <form onSubmit={handleSubmit}>
              <ReviewFields
                values={values}
                errors={errors}
                setFieldValue={setFieldValue}
                files={review?.files}
              />
              <div className="container-fluid">
                <div className="row">
                  <div className="col-12">
                    {showValidateBtn() && (
                      <button
                        type="submit"
                        className="btn btn-sm btn-success float-right mt-3 mb-3 d-inline-flex"
                        disabled={isSubmitting}
                        onClick={() =>
                          values?.state === 1
                            ? setSubmitType('validate')
                            : setSubmitType('add-review')
                        }
                      >
                        {values?.state === 1 ? 'Enviar' : 'Validar'}
                        {isSubmitting &&
                          (submitType === 'validate' ||
                            submitType === 'add-review') && (
                            <AppSpinner>
                              <AppRemixIcon icon="loader-4" className="ml-1" />
                            </AppSpinner>
                          )}
                      </button>
                    )}
                    {showSaveDraftBtn() && (
                      <button
                        type="submit"
                        className="btn btn-sm btn-outline-info float-right mt-3 mb-3 mr-2 d-inline-flex"
                        disabled={isSubmitting}
                        onClick={() => setSubmitType('draft')}
                      >
                        Guardar
                        {isSubmitting && submitType === 'draft' && (
                          <AppSpinner>
                            <AppRemixIcon icon="loader-4" className="ml-1" />
                          </AppSpinner>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </>
        )}
      </Formik>
      <AppModal title="Nueva revisión" size="lg" show={showValidateModal}>
        <Formik
          validateOnChange={false}
          validateOnBlur={false}
          initialValues={{
            scheduledReviewDate: addedReview?.scheduledReviewDate ?? '',
            description: '',
            requireDocumentation:
              review?.requireDocumentation !== null
                ? review?.requireDocumentation
                : review?.controlDocumentation === 2,
          }}
          onSubmit={handleEditAddedReview}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div>Edición de revisión de Control</div>
              <div className="container-fluid">
                <div className="row">
                  <div className="col-lg-6">
                    <AppInput
                      title="Identificador control asociado"
                      value={review?.controlCode || ''}
                      isReadOnly
                    />
                  </div>
                  <div className="col-lg-6">
                    <AppInput
                      title="Identificador revisión control"
                      value={addedReview?.code || ''}
                      isReadOnly
                    />
                  </div>
                  <div className="col-lg-12">
                    <AppTextArea
                      title="Descripción de revisión"
                      value={values.description}
                      error={errors.description}
                      onChange={(value) => setFieldValue('description', value)}
                    />
                  </div>
                  <div className="col-lg-6">
                    <AppDatePicker
                      locale={locale}
                      title="Fecha prevista revisión"
                      value={values.scheduledReviewDate}
                      onChange={(opt) =>
                        setFieldValue('scheduledReviewDate', opt)
                      }
                      popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                      //   minDate={Moment()
                      //     .add(1, 'days')
                      //     .toDate()}
                      showYearDropdown
                      showMonthDropdown
                    />
                  </div>
                  <div className="col-lg-6">
                    <AppSingleSelect
                      options={noYesOptions}
                      title="Requiere documentación"
                      value={values?.requireDocumentation}
                      error={errors?.requireDocumentation}
                      onChange={(opt) => {
                        setFieldValue('requireDocumentation', opt);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-12">
                  <button
                    type="submit"
                    className="btn btn-sm btn-success float-right"
                    disabled={isSubmitting}
                  >
                    Editar revisión de control
                  </button>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </AppModal>
    </>
  );
}
